// İSG Portal — erişilebilir tablo primitifleri (liste ekranları). Yatay taşma kendi
// kabında kaydırılır (sayfa gövdesi yatay kaymaz). Token'lı stil; literal renk yok.
//
// `carbon:` (S33 planı §2, Slice 1b) — Carbon'un yoğun DataTable'ı: kenarlıksız/
// gölgesiz Tile içinde oturur (dış çerçeve kalkar), üst bilgi satırı bold + normal
// harf büyüklüğü (uppercase/tracking KALDIRILIR — Carbon'un tüm etiket dilinde
// tekrar eden karar, bkz. plan §Stage 4), alt satırlar `border-b` (üstte değil) ve
// 40px yükseklik. `density` (README Stage 2 "sm 32 / md 40 / lg 48") tek bir CSS
// değişkenle (`--row-h`) taşınır — `Table` onu dış sarmalayıcıda TANIMLAR,
// `TableHead`/`TableRow` yalnız CARBON derlemesinde OKUR (base derlemede etkisiz,
// satır yüksekliği bugünkü `py-2.5` padding'inden gelmeye devam eder).

import type {
  CSSProperties,
  HTMLAttributes,
  ReactNode,
  TdHTMLAttributes,
  ThHTMLAttributes,
} from 'react';
import { cn } from './cn';

const DENSITY_PX: Record<'sm' | 'md' | 'lg', number> = { sm: 32, md: 40, lg: 48 };

/** Yatay kaydırılabilir kap + `<table>`. `caption` ekran-okur başlığıdır. */
export function Table({
  children,
  className,
  caption,
  density = 'md',
}: {
  children: ReactNode;
  className?: string;
  caption?: ReactNode;
  /** Carbon satır yoğunluğu (README Stage 2) — base derlemede görünmez etkisiz. */
  density?: 'sm' | 'md' | 'lg';
}) {
  return (
    <div
      className="w-full overflow-x-auto rounded-lg border border-border bg-surface carbon:rounded-none carbon:border-0"
      style={{ '--row-h': `${DENSITY_PX[density]}px` } as CSSProperties}
    >
      <table className={cn('w-full border-collapse text-sm', className)}>
        {caption ? <caption className="sr-only">{caption}</caption> : null}
        {children}
      </table>
    </div>
  );
}

export function TableHeader({ children, className }: { children: ReactNode; className?: string }) {
  return <thead className={cn('bg-surface-muted', className)}>{children}</thead>;
}

export function TableBody({ children, className }: { children: ReactNode; className?: string }) {
  return <tbody className={className}>{children}</tbody>;
}

export interface TableRowProps extends HTMLAttributes<HTMLTableRowElement> {
  /** Seçili satır (Sheet/detay açan Personnel tablosu, Slice 3) — sol 4px mavi çizgi. */
  selected?: boolean;
}

export function TableRow({ children, className, selected, ...props }: TableRowProps) {
  return (
    <tr
      data-state={selected ? 'selected' : undefined}
      className={cn(
        'border-t border-border first:border-t-0 hover:bg-surface-muted/60',
        'carbon:h-[var(--row-h)] carbon:border-t-0 carbon:border-b carbon:border-border',
        'carbon:hover:bg-surface-hover',
        'carbon:data-[state=selected]:bg-surface-muted',
        'carbon:data-[state=selected]:shadow-[inset_4px_0_0_var(--color-primary)]',
        className,
      )}
      {...props}
    >
      {children}
    </tr>
  );
}

export function TableHead({
  children,
  className,
  scope = 'col',
  ...props
}: ThHTMLAttributes<HTMLTableCellElement>) {
  return (
    <th
      scope={scope}
      className={cn(
        'whitespace-nowrap px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-muted',
        'carbon:h-[var(--row-h)] carbon:py-0 carbon:align-middle carbon:normal-case carbon:tracking-normal carbon:text-fg',
        className,
      )}
      {...props}
    >
      {children}
    </th>
  );
}

export function TableCell({
  children,
  className,
  truncate = true,
  ...props
}: TdHTMLAttributes<HTMLTableCellElement> & {
  /**
   * Carbon varsayılanı: hücre tek satıra kırpılır (`carbon:truncate`) — yoğun DataTable
   * satırının bugünkü davranışı, tüm mevcut tüketiciler ETKİLENMEZ. `false` (S33-8 review fix
   * #6 — `PermitsListPage`'in başlık hücresi 2 satıra sarabilsin diye) bu utility'yi HİÇ
   * EKLEMEZ; bir SONRAKİ `whitespace-normal`la "geri almaya" ÇALIŞMAZ — `truncate`
   * `tailwind-merge`de `overflow`/`whitespace`/`text-overflow` gruplarından AYRI kendi grubunda
   * (doğrulandı), iki çakışan bildirim CSS ÇIKTI SIRASINA kalırdı, class SIRASINA değil.
   */
  truncate?: boolean;
}) {
  return (
    <td
      className={cn(
        'px-4 py-2.5 align-middle text-fg',
        'carbon:h-[var(--row-h)] carbon:py-0',
        truncate && 'carbon:truncate',
        className,
      )}
      {...props}
    >
      {children}
    </td>
  );
}

/** Tüm sütunları kaplayan tek hücrelik satır (yükleniyor / boş / hata durumları). */
export function TableMessageRow({
  colSpan,
  children,
}: {
  colSpan: number;
  children: ReactNode;
}) {
  return (
    <tr>
      <td colSpan={colSpan} className="px-4 py-10 text-center text-sm text-muted">
        {children}
      </td>
    </tr>
  );
}
